import { useEffect, useRef, type JSX } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { LoadingState } from "../components/data/index.js";
import { Button } from "../components/ui/button.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card.js";
import {
  useAcceptInvitationMutation,
  useInvitationLookupQuery,
  type InvitationLookupResponse,
} from "../lib/api/invitations.js";
import type { UserRole } from "../lib/api/users.js";
import { clearAuthReturnTo } from "../lib/auth/return-to.js";
import { chooseCompany } from "../lib/company-session.js";
import { toErrorMessage } from "../lib/http.js";
import { enterApp } from "../lib/tenant-url.js";
import { useAuth } from "../providers/auth-provider.js";

const ROLE_LABELS: Record<UserRole, string> = {
  tenant_admin: "Admin",
  ops_manager: "Ops manager",
  store_manager: "Store manager",
  store_associate: "Store associate",
  field_auditor: "Field auditor",
  viewer: "Viewer",
};

export function AcceptInvitePage(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get("token");
  return <AcceptInvitation key={token} token={token} />;
}

function AcceptInvitation({ token }: { token: string | null }): JSX.Element {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  // Pre-flight lookup runs only when there's a token in the URL — see
  // useInvitationLookupQuery's `enabled` gate. We rely on this BEFORE the
  // login redirect so a stale/expired link tells the user directly instead
  // of forcing them through auth first.
  const lookup = useInvitationLookupQuery(token);

  const accept = useAcceptInvitationMutation({
    onSuccess: (data) => {
      // Hard-navigate INTO the joined tenant (/t/<code>/dashboard). A SPA
      // navigate would race the companies-list refetch — CompanyGate would see
      // the stale empty list and bounce to /onboarding. A full reload
      // re-bootstraps with the new-tenant cookie + fresh caches.
      clearAuthReturnTo();
      chooseCompany();
      enterApp(data.tenantCode);
    },
  });

  // Run once per authenticated recipient and token, including accepted-link recovery.
  const firedRef = useRef<string | null>(null);
  useEffect(() => {
    if (loading || !user || !token || firedRef.current === user.id) return;
    if (lookup.data?.status !== "pending" && lookup.data?.status !== "accepted") return;
    // Prefilled email is a convenience only: the server enforces the account
    // match, and a mismatch surfaces as a 403 handled in the error card below.
    firedRef.current = user.id;
    accept.mutate(token);
  }, [loading, user, token, accept, lookup.data?.status]);

  if (!token) {
    return (
      <main className="flex h-full items-center justify-center bg-background p-6">
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle>Invitation link is missing</CardTitle>
            <CardDescription>The link you followed does not include a token.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <Link to="/login">Back to login</Link>
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  if (token.length < 16 || token.length > 255) return <InvitationStateCard kind="invalid" />;

  if (loading || lookup.isPending) {
    return <LoadingState message="Checking invitation…" />;
  }

  // Unknown links are terminal; a temporary lookup failure stays retryable.
  if (lookup.isError) {
    if (lookup.error.status === 404) return <InvitationStateCard kind="invalid" />;
    return (
      <main className="flex h-full items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Could not check invitation</CardTitle>
            <CardDescription>{toErrorMessage(lookup.error, "Please try again.")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => void lookup.refetch()}>Try again</Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  const info = lookup.data;

  if (info.status === "expired") return <InvitationStateCard kind="expired" info={info} />;
  if (info.status === "cancelled") return <InvitationStateCard kind="cancelled" info={info} />;

  if (!user) {
    // Show the invitation details first; the user picks "Sign in" or
    // "Sign up". Both routes carry returnTo so post-auth bounces back
    // here and the auto-accept fires.
    const returnTo = encodeURIComponent(`/accept-invitation?token=${encodeURIComponent(token)}`);
    return (
      <main className="flex h-full items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>
              {info.status === "accepted" ? "Continue to" : "You’re invited to"} {info.companyName}
            </CardTitle>
            <CardDescription>
              <InvitationDetailLines info={info} />
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <Button asChild>
              <Link to={`/login?returnTo=${returnTo}`}>
                {info.status === "accepted" ? "Sign in to continue" : "Sign in to accept"}
              </Link>
            </Button>
            {info.status === "pending" ? (
              <Button variant="outline" asChild>
                <Link to={`/signup?returnTo=${returnTo}`}>Sign up to accept</Link>
              </Button>
            ) : null}
            <p className="text-xs text-muted-foreground">
              Sign in (or sign up) with the email address this invitation was sent to.
            </p>
          </CardContent>
        </Card>
      </main>
    );
  }

  // Authed; mutation is firing or has errored. The most common error is an
  // email mismatch (the invite is bound to a specific address; the server 403s
  // if the signed-in user isn't it) — so offer to switch accounts as well as
  // retry. The detail message comes from the server and is intentionally
  // generic (it never names the invited address).
  if (accept.isError) {
    const returnTo = encodeURIComponent(`/accept-invitation?token=${encodeURIComponent(token)}`);
    return (
      <main className="flex h-full items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Could not accept invitation</CardTitle>
            <CardDescription>
              {toErrorMessage(accept.error, "The link may be expired or invalid.")}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <Button
              onClick={() => {
                void logout()
                  .then(() => navigate(`/login?returnTo=${returnTo}`))
                  .catch(() => {
                    /* Stay here so sign-out can be retried. */
                  });
              }}
            >
              Sign in with a different account
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => accept.mutate(token)}>
                Try again
              </Button>
              <Button variant="ghost" asChild>
                <Link to="/select-company">Choose a company</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    );
  }

  // The effect starts acceptance after this render. Never redirect during
  // that gap: navigation can unmount the mutation before its success handler.
  return <LoadingState message={`Joining ${info.companyName}…`} />;
}

/**
 * Renders the friendly invitation detail lines (role, stores, who invited,
 * expiry) used in multiple cards. Pulled out so the Sign-in card and the
 * Accepted/Expired cards stay consistent.
 */
function InvitationDetailLines({ info }: { info: InvitationLookupResponse }): JSX.Element {
  return (
    <span className="block space-y-1">
      <span className="block">
        Role: <strong>{ROLE_LABELS[info.role] ?? info.role}</strong>
      </span>
      {info.storeNames.length > 0 ? (
        <span className="block">
          Stores:{" "}
          <strong>
            {info.storeNames.length <= 3
              ? info.storeNames.join(", ")
              : `${info.storeNames.slice(0, 3).join(", ")} +${info.storeNames.length - 3} more`}
          </strong>
        </span>
      ) : null}
      {info.invitedByEmail ? (
        <span className="block">
          Invited by <strong>{info.invitedByEmail}</strong>
        </span>
      ) : null}
    </span>
  );
}

interface InvitationStateCardProps {
  kind: "invalid" | "expired" | "cancelled";
  info?: InvitationLookupResponse;
}

function InvitationStateCard({ kind, info }: InvitationStateCardProps): JSX.Element {
  const copy = (() => {
    switch (kind) {
      case "invalid":
        return {
          title: "Invitation not found",
          description:
            "We couldn't find this invitation. The link may be malformed or already cleaned up — ask the person who invited you for a fresh link.",
        };
      case "expired":
        return {
          title: "Invitation expired",
          description: `This invitation${
            info?.companyName ? ` to ${info.companyName}` : ""
          } expired on ${
            info ? new Date(info.expiresAt).toLocaleDateString() : "an earlier date"
          }. Ask the person who invited you to send a new one.`,
        };
      case "cancelled":
        return {
          title: "Invitation revoked",
          description: `This invitation${
            info?.companyName ? ` to ${info.companyName}` : ""
          } was revoked. Ask for a new one if you still need access.`,
        };
    }
  })();
  return (
    <main className="flex h-full items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{copy.title}</CardTitle>
          <CardDescription>{copy.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-x-2">
          <Button asChild>
            <Link to="/login">Go to sign in</Link>
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}
